/**
 * JobDetailPage Component
 * 
 * Single job posting detail page displaying full job information.
 * Includes application functionality for authenticated employees.
 * 
 * @route /darbo-skelbimai/:id
 * 
 * Features:
 * - Display complete job information (title, salary, description, requirements)
 * - Kandidatavimas (Application) section for employees
 *   - CV selection (radio buttons)
 *   - Cover letter selection (dropdown)
 *   - Submit application
 * - Save/unsave job functionality
 * - Print job details
 * - Similar jobs section
 * - Custom success modal (no browser defaults)
 * 
 * User Access:
 * - All users: View job details
 * - Authenticated employees: Apply to jobs
 * - Employers: View their own job postings
 */

import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { 
  X, 
  ThumbsUp,
  Gift,
  Info,
  MapPin, 
  Briefcase, 
  Clock,
  Euro,
  Eye,
  Printer,
  Check,
  FileText
} from 'lucide-react';
import jobService, { JobWithEmployer } from '../services/jobService';
import savedJobService from '../services/savedJobService';
import cvTemplateService from '../services/cvTemplateService';
import JobCard from '../components/Jobs/JobCard';
import JobApplicationModal from '../components/Jobs/JobApplicationModal';
import { useAuth } from '../contexts/AuthContext';
import './JobDetailPage.scss';

const JobDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const fromRect = (location.state as any)?.fromRect as { x: number; y: number; width: number; height: number } | undefined;
  const [job, setJob] = useState<JobWithEmployer | null>(null);
  const [similarJobs, setSimilarJobs] = useState<JobWithEmployer[]>([]);
  const [loading, setLoading] = useState(true);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [cvs, setCvs] = useState<any[]>([]);
  const [coverLetters, setCoverLetters] = useState<any[]>([]);
  const [selectedCvId, setSelectedCvId] = useState<number | null>(null);
  const [selectedCoverLetterId, setSelectedCoverLetterId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [applicationError, setApplicationError] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [categoryImages, setCategoryImages] = useState<{ [value: string]: string | null }>({});
  
  // Ref for scrolling to kandidatavimas section
  const kandidatavimasRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchJobDetails = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const result = await jobService.getJobById(parseInt(id));
        
        if (result.success && result.data?.job) {
          setJob(result.data.job);
          
          // Fetch similar jobs via backend scoring (work_field > position_type > location > job_type > work_time)
          const similarResult = await jobService.getSimilarJobs(parseInt(id), 4);
          if (similarResult.success && similarResult.data?.jobs) {
            setSimilarJobs(similarResult.data.jobs);
          }

          // Check if user has applied and if job is saved
          if (isAuthenticated && (user?.user_type === 'employee' || user?.user_type === 'job_seeker')) {
            checkIfApplied(parseInt(id));
            checkIfSaved(parseInt(id));
            fetchCVsAndLetters();
          }
        }
      } catch (error) {
        console.error('Error fetching job details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchJobDetails();
    
    Promise.all([
      cvTemplateService.getCategoryImages('company_activity'),
      cvTemplateService.getCategoryImages('work_field')
    ]).then(([activityImages, workFieldImages]) => {
      setCategoryImages({ ...workFieldImages, ...activityImages });
    });
  }, [id, isAuthenticated, user]);

  const fetchCVsAndLetters = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const [cvsResponse, lettersResponse] = await Promise.all([
        fetch(`${API_URL}/cv/my`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(`${API_URL}/cover-letters/my`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
      ]);

      if (cvsResponse.ok) {
        const cvsResult = await cvsResponse.json();
        setCvs(cvsResult.data || []);
        
        // Auto-select primary CV if available
        const primaryCV = (cvsResult.data || []).find((cv: any) => cv.is_primary);
        if (primaryCV) {
          setSelectedCvId(primaryCV.id);
        }
      }

      if (lettersResponse.ok) {
        const lettersResult = await lettersResponse.json();
        setCoverLetters(lettersResult.data || []);
        
        // Auto-select primary cover letter if available
        const primaryLetter = (lettersResult.data || []).find((letter: any) => letter.is_primary);
        if (primaryLetter) {
          setSelectedCoverLetterId(primaryLetter.id);
        }
      }
    } catch (error) {
      console.error('Error fetching CVs and cover letters:', error);
    }
  };

  const checkIfApplied = async (jobId: number) => {
    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_URL}/job-applications/check/${jobId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        setHasApplied(result.data.hasApplied);
      }
    } catch (error) {
      console.error('Error checking application:', error);
    }
  };

  const checkIfSaved = async (jobId: number) => {
    try {
      const saved = await savedJobService.isJobSaved(jobId);
      setIsSaved(saved);
    } catch (error) {
      console.error('Error checking saved status:', error);
    }
  };


  const handleClose = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/darbo-skelbimai');
    }
  };

  const handleApply = () => {
    if (!isAuthenticated) {
      setApplicationError('Prašome prisijungti, kad galėtumėte kandidatuoti');
      // Scroll to kandidatavimas section anyway to show the error
      kandidatavimasRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    if (user?.user_type !== 'employee' && user?.user_type !== 'job_seeker') {
      setApplicationError('Tik darbuotojai gali kandidatuoti į pozicijas');
      kandidatavimasRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    if (hasApplied) {
      setApplicationError('Jūs jau kandidatavote į šią poziciją');
      kandidatavimasRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    
    // Scroll to kandidatavimas section
    kandidatavimasRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleApplicationSuccess = () => {
    setShowApplicationModal(false);
    setHasApplied(true);
    setShowSuccessModal(true);
  };

  const handleApplicationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedCvId) {
      setApplicationError('Prašome pasirinkti CV');
      return;
    }

    setSubmitting(true);
    setApplicationError('');

    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const response = await fetch(`${API_URL}/job-applications`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          job_id: job?.id,
          cv_id: selectedCvId,
          cover_letter_id: selectedCoverLetterId
        })
      });

      if (response.ok) {
        setHasApplied(true);
        setShowSuccessModal(true);
      } else {
        const result = await response.json();
        setApplicationError(result.message || 'Nepavyko pateikti paraiškos');
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      setApplicationError('Įvyko klaida pateikiant paraišką');
    } finally {
      setSubmitting(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSaveToggle = async () => {
    if (!isAuthenticated) {
      setApplicationError('Prašome prisijungti, kad galėtumėte išsaugoti darbo skelbimus');
      return;
    }

    if (user?.user_type !== 'employee' && user?.user_type !== 'job_seeker') {
      setApplicationError('Tik darbuotojai gali išsaugoti darbo skelbimus');
      return;
    }

    if (!job?.id || isSaving) return; // Prevent duplicate clicks

    // Optimistic UI update
    const previousState = isSaved;
    setIsSaved(!isSaved);
    setIsSaving(true);

    try {
      await savedJobService.toggleSaveJob(job.id, previousState);
    } catch (error) {
      console.error('Error toggling saved job:', error);
      // Revert optimistic update on error
      setIsSaved(previousState);
      setApplicationError('Nepavyko išsaugoti skelbimo. Bandykite dar kartą.');
    } finally {
      setIsSaving(false);
    }
  };

  const generateJobId = () => {
    if (!job?.id) return 'DS-00000';
    return `DS-${String(job.id).padStart(5, '0')}`;
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('lt-LT', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  const formatSalary = (): string => {
    if (!job) return '';
    if (job.hide_salary && !isAuthenticated) return 'Rodomas tik prisijungusiems';

    if (job.salary_min && job.salary_max) {
      return `${job.salary_min} - ${job.salary_max} €`;
    } else if (job.salary_min) {
      return `nuo ${job.salary_min} €`;
    } else if (job.salary_max) {
      return `iki ${job.salary_max} €`;
    }

    return 'Derinama';
  };

  const getCompanyLogoUrl = (filename?: string): string | null => {
    if (!filename) return null;
    const API_BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
    return `${API_BASE_URL}/uploads/profiles/${filename}`;
  };

  if (loading) {
    return (
      <div className="job-detail-loading">
        <div className="spinner"></div>
        <p>Kraunama...</p>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="job-detail-error">
        <h2>Skelbimas nerastas</h2>
        <button onClick={handleClose}>Grįžti atgal</button>
      </div>
    );
  }

  const logoUrl = getCompanyLogoUrl(job.company_logo);

  const originStyle = fromRect ? {
    '--origin-x': `${fromRect.x + fromRect.width / 2}px`,
    '--origin-y': `${fromRect.y + fromRect.height / 2}px`,
  } as React.CSSProperties : undefined;

  return (
    <div className={`job-detail-page ${fromRect ? 'animate-in' : ''}`} style={originStyle}>
      <div className="job-detail-overlay" onClick={handleClose}></div>
      
      <div className="job-detail-container">
        {/* Close Button */}
        <button className="close-btn" onClick={handleClose} data-tooltip="Uždaryti">
          <X size={24} />
        </button>

        {/* Job ID Header */}
        <div className="job-id-header">
          Skelbimo ID: {generateJobId()}
        </div>

        {/* Box 1: Company Info */}
        <div className="company-info-box">
          <div className="logo-section">
            {logoUrl ? (
              <img src={logoUrl} alt={job.company_name || 'Company logo'} />
            ) : (
              <div className="logo-placeholder">
                <Briefcase size={40} />
              </div>
            )}
            {job.website_url && (
              <a href={job.website_url} target="_blank" rel="noopener noreferrer" className="company-link">
                {job.website_url}
              </a>
            )}
          </div>

          <div className="description-section">
            <p>
              {(job as any).company_description || `${job.company_name || 'Ši įmonė'} – patikimas darbdavys, siūlantis įvairias karjeros galimybes. Prisijunkite prie mūsų komandos ir kurkite savo profesinę ateitį kartu su mumis.`}
            </p>
            
            <button 
              className="view-all-jobs-btn"
              onClick={() => navigate(`/darbo-skelbimai?employerId=${job.employer_id}&company=${encodeURIComponent(job.company_name || '')}`)}
              data-tooltip="Rodyti visus įmonės skelbimus"
            >
              <Eye size={16} />
              Rodyti visus įmonės skelbimus
            </button>
          </div>
        </div>

        {/* Client Info (for recruitment agency jobs) */}
        {(job as any).client_name && (
          <div className="client-info-box">
            <div className="client-info-box__label">Klientas</div>
            <h3 className="client-info-box__name">{(job as any).client_name}</h3>
            {(job as any).client_activity_sector && (
              <span className="client-info-box__sector">{(job as any).client_activity_sector}</span>
            )}
            {(job as any).client_descriptions && (job as any).client_descriptions.length > 0 && (
              <div className="client-info-box__descriptions">
                {(job as any).client_descriptions.map((desc: string, idx: number) => (
                  <p key={idx}>{desc}</p>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Box 2: Hero with Background Image + Apply Button + Job Title */}
        <div className="hero-action-box">
          <div 
            className="hero-background"
            style={(() => {
              const companyActivity = (job as any).company_activity || (job as any).employer_company_activity;
              const workField = (job as any).work_field;
              const imageUrl = (companyActivity && categoryImages[companyActivity])
                || (workField && categoryImages[workField])
                || null;
              if (imageUrl) {
                const API_BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
                const fullUrl = imageUrl.startsWith('http') ? imageUrl : `${API_BASE_URL}${imageUrl}`;
                return { backgroundImage: `url(${fullUrl})` };
              }
              return {};
            })()}
          ></div>
          <div className="hero-content">
            <div className="title-section">
              <h1>{job.title}</h1>
              <p className="company-name">{job.company_name}</p>
            </div>

            <div className="apply-section">
              <button 
                className="apply-button-main" 
                onClick={handleApply}
                disabled={hasApplied}
              >
                {hasApplied ? 'JAU KANDIDATAVOTE' : 'KANDIDATUOTI'}
              </button>
            </div>
          </div>
        </div>

        {/* Main Layout: Grid with 4 rows */}
        <div className="job-detail-layout">
          {/* Row 1: Darbo aprašymas */}
          <div className="nav-item">
            <div className="nav-icon" data-tooltip="Darbo aprašymas">
              <Printer size={20} />
            </div>
            <span>Darbo aprašymas</span>
          </div>
          <section className="content-box">
            <div 
              className="content-html" 
              dangerouslySetInnerHTML={{ __html: job.description || '<p>Nėra informacijos</p>' }} 
            />
          </section>

          {/* Row 2: Reikalavimai kandidatams */}
          <div className="nav-item">
            <div className="nav-icon" data-tooltip="Reikalavimai kandidatams">
              <ThumbsUp size={20} />
            </div>
            <span>Reikalavimai kandidatams</span>
          </div>
          <section className="content-box">
            <div 
              className="content-html" 
              dangerouslySetInnerHTML={{ __html: job.requirements || '<p>Nėra informacijos</p>' }} 
            />
          </section>

          {/* Row 3: Įmonė siūlo */}
          <div className="nav-item">
            <div className="nav-icon" data-tooltip="Įmonė siūlo">
              <Gift size={20} />
            </div>
            <span>Įmonė siūlo</span>
          </div>
          <section className="content-box">
            <div 
              className="content-html" 
              dangerouslySetInnerHTML={{ __html: job.company_offers || job.benefits || '<p>Nėra informacijos</p>' }} 
            />
          </section>

          {/* Row 4: Papildoma informacija */}
          <div className="nav-item">
            <div className="nav-icon" data-tooltip="Papildoma informacija">
              <Info size={20} />
            </div>
            <span>Papildoma informacija</span>
          </div>
          <section className="content-box">
            <div 
              className="content-html" 
              dangerouslySetInnerHTML={{ __html: job.additional_info || '<p>Nėra informacijos</p>' }} 
            />
          </section>

        </div>

        {/* Bottom Meta Information - Dates box + Meta 2x2 grid */}
        <div className="job-meta-footer">
          {/* Left: Dates box */}
          <div className="dates-box">
            <div className="date-row">
              <span className="date-label">Skelbimas paskelbtas</span>
              <span className="date-value">{formatDate(job.published_at)}</span>
            </div>
            <div className="date-row">
              <span className="date-label">Prašymus siųsti iki</span>
              <span className="date-value">{formatDate(job.application_deadline)}</span>
            </div>
          </div>

          {/* Right: Meta grid */}
          <div className="meta-grid">
            <div className="meta-cell">
              <span className="meta-icon-wrap" data-tooltip="Darbo vieta"><MapPin size={20} /></span>
              <div className="meta-content">
                <span className="meta-label">VIETA</span>
                <span className="meta-value">{job.location}</span>
              </div>
            </div>

            <div className="meta-cell">
              <span className="meta-icon-wrap" data-tooltip="Darbo laikas"><Clock size={20} /></span>
              <div className="meta-content">
                <span className="meta-label">DARBO LAIKAS</span>
                <span className="meta-value">{job.work_time || '-'}</span>
              </div>
            </div>

            <div className="meta-cell">
              <span className="meta-icon-wrap" data-tooltip="Darbo tipas"><Briefcase size={20} /></span>
              <div className="meta-content">
                <span className="meta-label">DARBO TIPAS</span>
                <span className="meta-value">{job.job_type || '-'}</span>
              </div>
            </div>

            <div className="meta-cell">
              <span className="meta-icon-wrap" data-tooltip="Atlyginimas"><Euro size={20} /></span>
              <div className="meta-content">
                <span className="meta-label">ATLYGINIMAS</span>
                <span className="meta-value">{formatSalary()}</span>
              </div>
            </div>

            {job.work_field && (
              <div className="meta-cell">
                <span className="meta-icon-wrap" data-tooltip="Darbo sritis"><Briefcase size={20} /></span>
                <div className="meta-content">
                  <span className="meta-label">DARBO SRITIS</span>
                  <span className="meta-value">{job.work_field}</span>
                </div>
              </div>
            )}

            {job.position_type && (
              <div className="meta-cell">
                <span className="meta-icon-wrap" data-tooltip="Pareigybės tipas"><Briefcase size={20} /></span>
                <div className="meta-content">
                  <span className="meta-label">PAREIGYBĖS TIPAS</span>
                  <span className="meta-value">{job.position_type}</span>
                </div>
              </div>
            )}

            {(job as any).start_date_type && (
              <div className="meta-cell">
                <span className="meta-icon-wrap" data-tooltip="Darbo pradžia"><Clock size={20} /></span>
                <div className="meta-content">
                  <span className="meta-label">DARBO PRADŽIA</span>
                  <span className="meta-value">{(job as any).start_date_type}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Additional Job Info */}
        <div className="job-additional-info">
          {(job as any).job_language && (
            <div className="info-row">
              <span className="info-label">Skelbimo kalba:</span>
              <span className="info-value">{(job as any).job_language}</span>
            </div>
          )}
          {job.work_field && (
            <div className="info-row">
              <span className="info-label">Darbo sritis:</span>
              <span className="info-value">{job.work_field}</span>
            </div>
          )}
          {job.position_type && (
            <div className="info-row">
              <span className="info-label">Pareigybės tipas:</span>
              <span className="info-value">{job.position_type}</span>
            </div>
          )}
          {(job as any).company_activity && (
            <div className="info-row">
              <span className="info-label">Įmonės veiklos sritis:</span>
              <span className="info-value">{(job as any).company_activity}</span>
            </div>
          )}
          {job.positions && (
            <div className="info-row">
              <span className="info-label">Pareigybės:</span>
              <span className="info-value">{job.positions}</span>
            </div>
          )}
          {(job as any).start_date_type && (
            <div className="info-row">
              <span className="info-label">Darbo pradžia:</span>
              <span className="info-value">{(job as any).start_date_type}</span>
            </div>
          )}
          {(job as any).contact_email && (
            <div className="info-row">
              <span className="info-label">Kontaktinis el. paštas:</span>
              <span className="info-value">{(job as any).contact_email}</span>
            </div>
          )}
          {job.responsibilities && (
            <div className="info-row full-width">
              <span className="info-label">Atsakomybės:</span>
              <div 
                className="info-html" 
                dangerouslySetInnerHTML={{ __html: job.responsibilities }} 
              />
            </div>
          )}
          
          {/* Benefits section (separate from company offers) */}
          {job.benefits && job.benefits !== (job as any).company_offers && (
            <div className="info-row full-width">
              <span className="info-label">Privalumai:</span>
              <div 
                className="info-html" 
                dangerouslySetInnerHTML={{ __html: job.benefits }} 
              />
            </div>
          )}

          {/* Badges */}
          {(!!job.guarantee_confidentiality || !!job.inform_selected_only) && (
            <div className="info-badges">
              {!!job.guarantee_confidentiality && (
                <span className="info-badge success">✓ Konfidencialumą garantuojame</span>
              )}
              {!!job.inform_selected_only && (
                <span className="info-badge info">ℹ Informuosime tik atrinktus kandidatus</span>
              )}
            </div>
          )}
        </div>

        {/* Kandidatavimas Section */}
        {isAuthenticated && (user?.user_type === 'employee' || user?.user_type === 'job_seeker') && !hasApplied ? (
          <div className="kandidatavimas-section" ref={kandidatavimasRef}>
            <div style={{ width: '100%' }}>
              <h2 className="section-title">Kandidatavimas</h2>
              
              {applicationError && (
                <div className="error-message">{applicationError}</div>
              )}

              {cvs.length === 0 ? (
                <div className="no-cv-message">
                  <p>Neturite sukurto CV. Prašome pirmiausia sukurti CV.</p>
                  <button className="btn-create-cv" onClick={() => navigate('/cv/create')}>
                    Sukurti CV
                  </button>
                </div>
              ) : (
                <form onSubmit={handleApplicationSubmit} className="application-form">
                  <div className="form-group">
                    <label className="form-label-with-icon">
                      <FileText size={18} />
                      Pasirinkite CV
                    </label>
                    <div className="radio-group">
                      {cvs.map((cv) => (
                        <label key={cv.id} className="radio-option">
                          <input 
                            type="radio"
                            name="cv-selection"
                            value={cv.id}
                            checked={selectedCvId === cv.id}
                            onChange={() => setSelectedCvId(cv.id)}
                            required
                          />
                          <span className="radio-text">
                            {cv.title} {cv.is_primary && <span className="primary-badge">(pagrindinis)</span>}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="letter-select" className="form-label-with-icon cover-letter-label">
                      <FileText size={18} />
                      Pridėkite motyvacinį laišką <span className="optional">(neprivaloma)</span>
                    </label>
                    <select 
                      id="letter-select"
                      value={selectedCoverLetterId || ''}
                      onChange={(e) => setSelectedCoverLetterId(e.target.value ? Number(e.target.value) : null)}
                      className="form-select"
                    >
                      <option value="">-- Pasirinkite motyvacinį laišką --</option>
                      {coverLetters.map((letter) => (
                        <option key={letter.id} value={letter.id}>
                          {letter.title} {letter.is_primary ? '(pagrindinis)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-actions">
                    <button 
                      type="submit" 
                      className="btn-submit"
                      disabled={submitting || !selectedCvId}
                    >
                      {submitting ? 'SIUNČIAMA...' : 'KANDIDATUOTI'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        ) : !isAuthenticated ? (
          <div className="kandidatavimas-section" ref={kandidatavimasRef}>
            <div className="apply-guest-prompt">
              <h2 className="section-title">Kandidatavimas</h2>
              <p>Norėdami aplikuoti į šį skelbimą, prašome prisijungti.</p>
              <div className="apply-guest-actions">
                <Link to={`/login?return=${encodeURIComponent(`/job/${id}`)}`} className="btn-submit">Prisijungti</Link>
                <Link to={`/register?return=${encodeURIComponent(`/job/${id}`)}`} className="btn-create-cv">Registruotis</Link>
              </div>
            </div>
          </div>
        ) : null}

        {/* Similar Jobs */}
        {similarJobs.length > 0 && (
          <div className="similar-jobs-section">
            <h2>Panašūs skelbimai</h2>
            <div className="similar-jobs-list">
              {similarJobs.map((similarJob) => {
                const similarCompanyActivity = (similarJob as any).company_activity || (similarJob as any).employer_company_activity;
                const similarWorkField = (similarJob as any).work_field;
                const categoryImageUrl = (similarCompanyActivity && categoryImages[similarCompanyActivity])
                  || (similarWorkField && categoryImages[similarWorkField])
                  || null;
                return (
                  <JobCard 
                    key={similarJob.id} 
                    job={similarJob} 
                    layout="list"
                    categoryImageUrl={categoryImageUrl}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* Application Modal */}
        {showApplicationModal && job && job.id && job.title && (
          <JobApplicationModal
            jobId={job.id}
            jobTitle={job.title}
            companyName={job.company_name || 'Company'}
            onClose={() => setShowApplicationModal(false)}
            onSuccess={handleApplicationSuccess}
          />
        )}
      </div>

      {/* Success Modal - Rendered at document root using Portal */}
      {showSuccessModal && ReactDOM.createPortal(
        <div className="success-modal-overlay" onClick={() => setShowSuccessModal(false)}>
          <div className="success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon">
              <Check size={48} />
            </div>
            <h2>Jūsų paraiška sėkmingai pateikta!</h2>
            <p>Darbdavys susisieks su jumis artimiausiu metu.</p>
            <button className="btn-close-success" onClick={() => setShowSuccessModal(false)}>
              <X size={24} />
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default JobDetailPage;
